// Copyright 2019 Sorama B.V.

import { SVGNode } from "libraries/SVG/SVGNode";
import { HorizontalAnchor, ISVGComposer, RelativePosition, VerticalAnchor } from "./ISVGComposer";

interface Bounds {
    x: number;
    y: number;
    width: number;
    height: number;
}

export class SVGComposer implements ISVGComposer {
    private readonly originalNode: SVGNode;
    private node: SVGNode;

    private horizontalAnchor: HorizontalAnchor = HorizontalAnchor.Middle;
    private verticalAnchor: VerticalAnchor = VerticalAnchor.Middle;

    private transformers: (() => void)[] = [];

    constructor(node: SVGNode) {
        this.checkNode(node);
        this.originalNode = node;
    }

    public anchor(horizontal: HorizontalAnchor, vertical: VerticalAnchor): ISVGComposer {
        this.transformers.push(() => {
            this.horizontalAnchor = horizontal;
            this.verticalAnchor = vertical;
        });

        return this;
    }

    public append(node: SVGNode, position: RelativePosition): ISVGComposer {
        this.checkNode(node);

        this.transformers.push(() => {
            // Merge two nodes
            const newNode = new SVGNode("svg");

            // Determine new bounds
            const b0 = this.node.bounds;
            const b1 = node.bounds;

            const newBounds: Bounds = { x: 0, y: 0, width: 0, height: 0 };
            let t0 = "";
            let t1 = "";

            switch (position) {
                case RelativePosition.Bottom:
                    newBounds.x = 0;
                    newBounds.y = 0;
                    newBounds.width = Math.max(b0.width, b1.width);
                    newBounds.height = b0.height + b1.height;

                    t0 =
                        "translate(" +
                        (this.getTransformOffset(this.horizontalAnchor, newBounds.width, b0.width) - b0.x) +
                        "," +
                        -b0.y +
                        ")";
                    t1 =
                        "translate(" +
                        (this.getTransformOffset(this.horizontalAnchor, newBounds.width, b1.width) - b1.x) +
                        "," +
                        (b0.height - b1.y) +
                        ")";
                    break;
                case RelativePosition.Right:
                    newBounds.x = 0;
                    newBounds.y = 0;
                    newBounds.width = b0.width + b1.width;
                    newBounds.height = Math.max(b0.height, b1.height);

                    t0 =
                        "translate(" +
                        -b0.x +
                        "," +
                        (this.getTransformOffset(this.verticalAnchor, newBounds.height, b0.height) - b0.y) +
                        ")";
                    t1 =
                        "translate(" +
                        (b0.width - b1.x) +
                        "," +
                        (this.getTransformOffset(this.verticalAnchor, newBounds.height, b1.height) - b1.y) +
                        ")";

                    break;
                default:
                    throw new Error("Not implemented");
            }

            const n0 = this.node.transform("g");
            const n1 = node.transform("g");

            newNode.setAttribute("viewBox", this.toViewBox(newBounds));

            n0.setAttribute("transform", t0);
            n0.setAttribute("part-size", this.toViewBox(b0));
            n1.setAttribute("transform", t1);
            n1.setAttribute("part-size", this.toViewBox(b1));

            newNode.addChild(n0);
            newNode.addChild(n1);

            this.node = newNode;
        });

        return this;
    }

    public remove(part: string): ISVGComposer {
        this.transformers.push(() => {
            // Find and remove part
            this.removePart(this.node, part);
        });

        return this;
    }

    public trim(): ISVGComposer {
        this.transformers.push(() => {
            // Find the minimum size for top level svg by finding part sizes and offsets in the svg tree
            const bounds = this.trimSize(this.node);
            this.node.setAttribute("viewBox", this.toViewBox(bounds));
        });

        return this;
    }

    public generate(): SVGNode {
        // Run all transformers and return the svg node
        this.node = this.originalNode.clone();
        for (const transformer of this.transformers) {
            transformer();
        }

        const node = this.node;
        this.node = undefined;
        return node;
    }

    private checkNode(node: SVGNode): void {
        if (node.tag !== "svg") {
            throw new Error("The provided SVG node is not a top level node");
        }

        if (!node.bounds) {
            throw new Error("The provided SVG node does not have dimensions");
        }
    }

    private getTransformOffset(anchorPoint: number, space: number, size: number): number {
        switch (anchorPoint) {
            case -1:
                return 0;

            case 0:
                return (space - size) / 2;

            case 1:
                return space - size;

            default:
                throw new Error("Invalid anchorpoint");
        }
    }

    private removePart(node: SVGNode, part: string): void {
        for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];
            if (child.part === part) {
                node.removeChild(i);
                i--;
            } else {
                this.removePart(child, part);
            }
        }
    }

    private trimSize(node: SVGNode): Bounds {
        const translateRegex = /translate\(\s*(-?[0-9]+\.?[0-9]*)\s*,?\s*(-?[0-9]+\.?[0-9]*)\s*\)/;

        let bounds = {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
        };

        let minOffsetX = Number.MAX_VALUE;
        let minOffsetY = Number.MAX_VALUE;

        for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];

            const transform = child.attributes["transform"];
            const partSize = child.attributes["part-size"];

            if (!partSize) {
                bounds = this.mergeBounds(bounds, this.trimSize(child));
                continue;
            }

            const offset = transform.match(translateRegex);
            let offX = 0;
            let offY = 0;
            if (offset && offset.length == 3) {
                offX = Number(offset[1]);
                offY = Number(offset[2]);
            }

            let x = 0;
            let y = 0;
            let w = 0;
            let h = 0;
            if (partSize) {
                const splits = partSize.split(",");
                if (splits && splits.length === 4) {
                    x = Number(splits[0]);
                    y = Number(splits[1]);
                    w = Number(splits[2]);
                    h = Number(splits[3]);
                }
            }

            minOffsetX = Math.min(offX + x, minOffsetX);
            minOffsetY = Math.min(offY + y, minOffsetY);

            x += offX;
            y += offY;

            // Merge the existing bound and the new bound
            bounds = this.mergeBounds(bounds, { x: x, y: y, width: w, height: h });
        }

        bounds.width -= minOffsetX;
        bounds.height -= minOffsetY;

        if (minOffsetX !== 0 || minOffsetY !== 0) {
            for (let i = 0; i < node.children.length; i++) {
                const child = node.children[i];

                const transform = child.attributes["transform"];

                if (!transform) continue;

                const offset = transform.match(translateRegex);
                if (offset && offset.length == 3) {
                    const newTransform =
                        "translate(" + (Number(offset[1]) - minOffsetX) + "," + (Number(offset[2]) - minOffsetY) + ")";
                    child.setAttribute("transform", newTransform);
                }
            }
        }

        return bounds;
    }

    private mergeBounds(b0: Bounds, b1: Bounds): Bounds {
        // Merge the existing bound and the new bound
        if (b1.x < b0.x) {
            b0.width += b0.x - b1.x;
            b0.x = b1.x;
        }

        if (b1.y < b0.y) {
            b0.height += b0.y - b1.y;
            b0.y = b1.y;
        }

        if (b1.x + b1.width > b0.x + b0.width) {
            b0.width = b1.x + b1.width - b0.x;
        }

        if (b1.y + b1.height > b0.y + b0.height) {
            b0.height = b1.y + b1.height - b0.y;
        }

        return b0;
    }

    private toViewBox(bounds: Bounds) {
        return (
            bounds.x.toFixed(2) +
            "," +
            bounds.y.toFixed(2) +
            "," +
            bounds.width.toFixed(2) +
            "," +
            bounds.height.toFixed(2)
        );
    }
}
